function optionGroupCountVariantSelects(vs) {
  let n = vs.querySelectorAll(':scope > fieldset, :scope > .product-form__input').length;
  if (n) return n;
  n = vs.querySelectorAll('select[name*="options"]').length;
  if (n) return n;
  const vr = vs.querySelector('variant-radios');
  if (vr) {
    n = vr.querySelectorAll('fieldset, .product-form__input').length;
    if (n) return n;
  }
  return 0;
}

function getSelectedOptionValuesFromVariantSelects(vs) {
  const values = [];
  vs.querySelectorAll(':scope > fieldset, :scope > .product-form__input').forEach((wrapper) => {
    const select = wrapper.querySelector('select');
    if (select?.selectedOptions?.[0]) {
      const value = select.selectedOptions[0].value;
      // Ignora opção vazia inicial
      if (value !== '') {
        values.push(value);
      }
      return;
    }
    const checked = wrapper.querySelector('input[type="radio"]:checked');
    if (checked) values.push(checked.value);
  });
  if (values.length) return values;
  vs.querySelectorAll('select[name*="options"]').forEach((sel) => {
    if (sel.selectedOptions?.[0]) {
      const value = sel.selectedOptions[0].value;
      // Ignora opção vazia inicial
      if (value !== '') {
        values.push(value);
      }
    }
  });
  if (values.length) return values;
  vs.querySelectorAll('input[type="radio"][name^="options"]:checked').forEach((r) => values.push(r.value));
  if (values.length) return values;
  const vr = vs.querySelector('variant-radios');
  if (vr) vr.querySelectorAll('input[type="radio"]:checked').forEach((r) => values.push(r.value));
  return values;
}

function getExpectedVariantFromPicker(productFormElement) {
  const productInfo = productFormElement.closest('product-info');
  const variantSelects = productInfo?.querySelector('variant-selects');
  if (!variantSelects) {
    return { skipValidation: true, ok: true, variantId: null, variant: null };
  }

  const jsonEl = variantSelects.querySelector('#ProductJson');
  if (!jsonEl) {
    return { skipValidation: false, ok: false, variantId: null, variant: null };
  }

  let variants;
  try {
    variants = JSON.parse(jsonEl.textContent);
  } catch (e) {
    return { skipValidation: false, ok: false, variantId: null, variant: null };
  }

  const selectedStrings = getSelectedOptionValuesFromVariantSelects(variantSelects);
  const groups = optionGroupCountVariantSelects(variantSelects);
  if (groups > 0 && selectedStrings.length !== groups) {
    return { skipValidation: false, ok: false, variantId: null, variant: null };
  }

  if (!selectedStrings.length) {
    return { skipValidation: false, ok: false, variantId: null, variant: null };
  }

  const variant = variants.find((v) =>
    selectedStrings.every((val, idx) => String(v[`option${idx + 1}`] ?? '') === String(val))
  );

  if (!variant) {
    return { skipValidation: false, ok: false, variantId: null, variant: null };
  }

  return { skipValidation: false, ok: true, variantId: String(variant.id), variant: variant };
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Inicializa tooltip para botão desabilitado
        this.initDisabledButtonTooltip();
      }

      initDisabledButtonTooltip() {
        this.disabledOverlay = this.querySelector('[data-disabled-overlay]');
        this.tooltip = this.querySelector('[data-tooltip]');
        
        if (!this.disabledOverlay || !this.tooltip) return;
        
        let tooltipTimeout;
        
        this.disabledOverlay.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Mostra o tooltip
          this.tooltip.removeAttribute('hidden');
          this.tooltip.classList.add('show');
          
          // Remove tooltip anterior se existir
          clearTimeout(tooltipTimeout);
          
          // Esconde após 3 segundos
          tooltipTimeout = setTimeout(() => {
            this.tooltip.classList.remove('show');
            setTimeout(() => {
              this.tooltip.setAttribute('hidden', '');
            }, 200);
          }, 3000);
        });
      }

      connectedCallback() {
        this._variantChangeUnsub?.();
        this._optionSelectionUnsub?.();

        const productInfo = this.closest('product-info');
        const variantSelects = productInfo?.querySelector('variant-selects');

        if (!variantSelects) {
          this.classList.add('variant-picker--ready');
          return;
        }

        const setVariantGuardDisabled = (on) => {
          if (!this.submitButton) return;
          if (on) {
            this.submitButton.dataset.variantGuardDisabled = 'true';
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButton.setAttribute('aria-disabled', 'true');
            // Mostra o overlay para capturar cliques
            if (this.disabledOverlay) {
              this.disabledOverlay.removeAttribute('hidden');
            }
          } else {
            delete this.submitButton.dataset.variantGuardDisabled;
            delete this.submitButton.dataset.awaitingVariantInit;
            if (!this.submitButton.hasAttribute('data-sold-out-lock')) {
              this.submitButton.removeAttribute('disabled');
              this.submitButton.removeAttribute('aria-disabled');
            }
            // Esconde o overlay quando habilitado
            if (this.disabledOverlay) {
              this.disabledOverlay.setAttribute('hidden', '');
            }
            // Esconde o tooltip se estiver visível
            if (this.tooltip) {
              this.tooltip.classList.remove('show');
              this.tooltip.setAttribute('hidden', '');
            }
          }
        };

        const markReadyIfSynced = () => {
          const expected = getExpectedVariantFromPicker(this);
          const current = this.variantIdInput?.value;
          const synced =
            expected.skipValidation ||
            (expected.ok && expected.variantId && String(current) === expected.variantId);

          if (synced) {
            this.classList.add('variant-picker--ready');
            // Se a variante está selecionada mas indisponível, mostra sold out
            if (expected.variant && !expected.variant.available) {
              this.submitButton.setAttribute('disabled', 'disabled');
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButton.setAttribute('data-sold-out-lock', '');
              this.submitButtonText.textContent = window.variantStrings?.soldOut || 'Esgotado';
              // Esconde overlay pois não é aguardando seleção
              if (this.disabledOverlay) {
                this.disabledOverlay.setAttribute('hidden', '');
              }
            } else {
              setVariantGuardDisabled(false);
            }
          } else {
            this.classList.remove('variant-picker--ready');
            setVariantGuardDisabled(true);
          }
        };

        markReadyIfSynced();
        requestAnimationFrame(() => {
          requestAnimationFrame(markReadyIfSynced);
        });

        this._optionSelectionUnsub = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          const tgt = data?.event?.target;
          if (!tgt || !variantSelects.contains(tgt)) return;
          this.classList.remove('variant-picker--ready');
          setVariantGuardDisabled(true);
        });

        this._variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const pi = this.closest('product-info');
          if (!pi) return;
          const evtSection = event?.data?.sectionId;
          const mySection = pi.dataset.originalSection || pi.dataset.section;
          if (evtSection && mySection && evtSection !== mySection) return;
          queueMicrotask(markReadyIfSynced);
        });
      }

      disconnectedCallback() {
        this._variantChangeUnsub?.();
        this._optionSelectionUnsub?.();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || !this.form) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const mismatchMsg =
          window.variantStrings?.variant_reselect_before_cart ||
          window.variantStrings?.variant_selection_mismatch ||
          'Selecione o tamanho novamente antes de adicionar ao carrinho.';

        const expected = getExpectedVariantFromPicker(this);
        const submittedId = this.variantIdInput?.value;

        if (!expected.skipValidation) {
          if (!expected.ok || !expected.variantId || String(submittedId) !== expected.variantId) {
            this.handleErrorMessage(mismatchMsg);
            return;
          }
          
          // Verifica se a variante selecionada está disponível
          if (expected.variant && !expected.variant.available) {
            this.handleErrorMessage(window.variantStrings?.soldOut || 'Este produto está esgotado.');
            return;
          }
        }

        const vsEl = this.closest('product-info')?.querySelector('variant-selects');
        if (
          vsEl &&
          !this.classList.contains('variant-picker--ready') &&
          !vsEl.classList.contains('is-ready')
        ) {
          this.handleErrorMessage(mismatchMsg);
          return;
        }

        if (this.closest('product-info')?.querySelector('variant-selects') && this.form?.dataset.variantReady === 'false') {
          this.handleErrorMessage(mismatchMsg);
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        const hasVariantPicker = !!this.closest('product-info')?.querySelector('variant-selects');

        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          
          // Verifica se é sold out ou aguardando seleção
          const isSoldOut = text === window.variantStrings?.soldOut || 
                           text === window.variantStrings?.unavailable;
          
          if (hasVariantPicker && !isSoldOut) {
            // Aguardando seleção - mostra overlay
            this.submitButton.setAttribute('data-awaiting-variant-init', 'true');
            if (this.disabledOverlay) {
              this.disabledOverlay.removeAttribute('hidden');
            }
          } else {
            // Sold out - esconde overlay
            this.submitButton.setAttribute('data-sold-out-lock', '');
            delete this.submitButton.dataset.variantGuardDisabled;
            delete this.submitButton.dataset.awaitingVariantInit;
            if (this.disabledOverlay && hasVariantPicker) {
              this.disabledOverlay.setAttribute('hidden', '');
            }
          }
          
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('data-sold-out-lock');
          if (hasVariantPicker && !this.classList.contains('variant-picker--ready')) {
            this.submitButton.setAttribute('disabled', 'disabled');
            this.submitButton.setAttribute('aria-disabled', 'true');
            this.submitButton.dataset.variantGuardDisabled = 'true';
            // Mostra overlay quando aguardando seleção
            if (this.disabledOverlay) {
              this.disabledOverlay.removeAttribute('hidden');
            }
          } else {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            delete this.submitButton.dataset.variantGuardDisabled;
            // Esconde overlay quando habilitado
            if (this.disabledOverlay) {
              this.disabledOverlay.setAttribute('hidden', '');
            }
          }
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
